// Reads the amount owed off an invoice's text.
//
// Used where an invoice PDF arrives: the email intake (api/_invoice-amount.js) and the
// Recover from aid upload (src/lib/budgetRecoup.js). Pure, so it runs the same in the
// browser, on the server and in tests.
//
// The senders are clerks, report writers and barristers, and each lays the total out
// differently. What the text looks like once a PDF reader has pulled it out:
//   Meldrums, Chapman's  "AMOUNT DUE (incl GST, $33.55) $369.00"   - GST figure first
//   Greens               "AMOUNT DUE $579.00"
//   Parnell's, Holmes    "Total Payable $1,342.10"
//   Foley's              "BALANCE DUE $1,158.00"                    - label split in two
//   Xero (report writers)"Amount Due AUD 1,650.00"                  - no dollar sign
// Some PDFs come out of order: "Total Payable" lands paragraphs away from its figure, with
// the legal-costs notice in between. Those bills state their GST, so the second pass takes
// the figure that is eleven times it. When neither pass is sure, nothing is returned: a
// blank amount is typed in by a person, a wrong one might be paid.
//
// A result is only ever a starting point. It is shown as read from the PDF, and a person
// confirms it before anything is paid.

const MONEY = String.raw`\$?\s*(\d{1,3}(?:,\d{3})+(?:\.\d{2})?|\d+\.\d{2})`;

// Strongest first. Each must be followed closely by its figure.
const LABELS = [
  String.raw`amount\s+due(?:\s+aud)?`,
  String.raw`balance\s+due`,
  String.raw`total\s+(?:amount\s+)?(?:due|payable|owing)`,
  String.raw`amount\s+payable`,
  String.raw`total\s*\(?\s*(?:inc|incl|including)\.?\s*gst\s*\)?`,
  String.raw`invoice\s+total`,
  // Weakest: a bare "Total" straight before its figure. "TOTAL EX GST" and "TOTAL GST" are
  // not followed directly by a figure, so they do not match.
  String.raw`total`,
];

// Upper bound for a fee or report. Anything larger is far more likely a misread (a footer
// running into a figure) than a real invoice.
const MAX_AMOUNT = 100000;

const toNumber = (raw) => Number(String(raw).replace(/[$,\s]/g, ""));
const plausible = (n) => Number.isFinite(n) && n > 0 && n < MAX_AMOUNT;

function flatten(text) {
  return String(text || "")
    .replace(/\s+/g, " ")
    // "(incl GST, $33.55)" sits between the label and the figure on several clerks' bills.
    // Only a note with a figure in it: a bare "(inc GST)" is part of a label.
    .replace(/\(\s*(?:inc|incl|including)\.?\s*gst[^)]*\$[^)]*\)/gi, " ")
    // The legal-costs notice every barrister's bill carries: "less than $100,000 (indexed)".
    .replace(/\$\s*\d[\d,]*(?:\.\d+)?\s*\(indexed\)/gi, " ")
    .replace(/\s+/g, " ");
}

function labelled(flat) {
  for (const label of LABELS) {
    // (?![a-z]) rather than \b: a label can end in ")", where \b never matches.
    const pattern = new RegExp(String.raw`\b${label}(?![a-z])\s*[:\-]?\s*${MONEY}`, "gi");
    const found = [...flat.matchAll(pattern)].map((m) => toNumber(m[1])).filter(plausible);
    // The last on the page is the grand total, not a subtotal.
    if (found.length) return found[found.length - 1];
  }
  return null;
}

// A bill that states its GST: the total is the figure on the page that is eleven times it.
// "The amount of GST included in the above supply is $52.64" -> $579.00. Only a figure the
// page actually prints is returned, never the product itself, so arithmetic on a misread
// GST line cannot invent an amount.
function fromGst(raw) {
  const text = String(raw || "").replace(/\s+/g, " ");
  const gst = text.match(/gst\s+(?:included\s+in\s+the\s+above\s+supply\s+is|incl(?:uded)?\.?,?)\s*\$\s*(\d[\d,]*\.\d{2})/i);
  if (!gst) return null;
  const expected = toNumber(gst[1]) * 11;
  const figures = [...text.matchAll(/\$?\s*(\d{1,3}(?:,\d{3})+\.\d{2}|\d+\.\d{2})/g)]
    .map((m) => toNumber(m[1]))
    .filter((n) => plausible(n) && Math.abs(n - expected) <= 0.1);
  return figures.length ? Math.max(...figures) : null;
}

// Returns the amount as a string with two decimals ("1342.10"), or "" when none is found.
export function readInvoiceAmount(text = "") {
  const flat = flatten(text);
  if (!flat.trim()) return "";
  const amount = labelled(flat) ?? fromGst(text);
  return amount == null ? "" : amount.toFixed(2);
}
